// 🎨 Theme Store - Gestión del tema global
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getTheme, defaultTheme, temasPermitidos, type Theme } from '@/lib/constants/themes';
import { temaMarca } from '@brand/tema';

interface ThemeStore {
  currentThemeId: string;
  theme: Theme;
  isDarkMode: boolean;
  setTheme: (themeId: string) => void;
  toggleDarkMode: () => void;
  applyTheme: (theme: Theme) => void;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set, get) => ({
      currentThemeId: defaultTheme,
      theme: getTheme(defaultTheme),
      isDarkMode: false,

      setTheme: (themeId: string) => {
        const theme = getTheme(themeId);
        set({
          currentThemeId: themeId,
          theme,
          isDarkMode: theme.category === 'dark',
        });
        get().applyTheme(theme);

        // Sincronizar con backend si está autenticado
        try {
          fetch('/api/preferences', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ theme: themeId }),
          }).catch(() => {
            // Ignorar errores de sincronización
          });
        } catch {
          // Ignorar errores
        }
      },

      toggleDarkMode: () => {
        // Marca con pareja claro↔oscuro (TrackConstruction): se cambia de TEMA, no solo la
        // clase .dark (las variables en línea del tema le ganarían a la clase).
        const par = temaMarca.parOscuro?.[get().currentThemeId];
        if (par) { get().setTheme(par); return; }
        const { isDarkMode } = get();
        const newIsDarkMode = !isDarkMode;

        set({ isDarkMode: newIsDarkMode });

        if (newIsDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },

      applyTheme: (theme: Theme) => {
        const root = document.documentElement;

        // Aplicar variables CSS
        Object.entries(theme.colors).forEach(([key, value]) => {
          const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
          root.style.setProperty(cssVarName, value);
        });

        // Aplicar clase dark si corresponde
        if (theme.category === 'dark') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }

        // Marcar el tema activo en el DOM para CSS con scope (p. ej. capa HUD de Stark)
        root.dataset.theme = theme.id;
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Tema guardado que esta instalación no ofrece → el de la marca
          if (!temasPermitidos(state.theme)) {
            state.currentThemeId = defaultTheme;
            state.theme = getTheme(defaultTheme);
            state.isDarkMode = state.theme.category === 'dark';
          }
          state.applyTheme(state.theme);
        }
      },
    }
  )
);
